import React, { JSX } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/password-change-form.en.t.json';
import Keys from '../../../_Translations/generated/password-change-form.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import PasswordChangeFormType from './PasswordChangeFormType';
import InfoPopover from '../CamperItemList/InfoPopover';
import { sanitize } from '@ibe/services';

export interface Props {
  onSubmit: (values: PasswordChangeFormType) => void;
  withOldPassword?: boolean;
  passwordComplexityInfo?: string;
}

type PasswordChangeFormValues = PasswordChangeFormType & {
  passwordConfirmation: string;
};

const PasswordChangeForm = (props: Props): JSX.Element => {
  const { t } = useTranslation('password-change-form', fallback, Keys);

  const { onSubmit, withOldPassword, passwordComplexityInfo } = props;

  const oldPasswordSchema = withOldPassword
    ? yup.string().required(t(Keys.requiredField))
    : yup.string().defined();

  const schema: yup.ObjectSchema<PasswordChangeFormValues> = yup.object({
    oldPassword: oldPasswordSchema,
    password: yup.string().required(t(Keys.requiredField)),
    passwordConfirmation: yup
      .string()
      .required(t(Keys.requiredField))
      .oneOf([yup.ref('password')], t(Keys.passwordsMustMatch))
  });

  const defaultValues: PasswordChangeFormValues = {
    oldPassword: '',
    password: '',
    passwordConfirmation: ''
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<PasswordChangeFormValues, unknown, PasswordChangeFormValues>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const submit: SubmitHandler<PasswordChangeFormValues> = (values): void => {
    const { passwordConfirmation: _passwordConfirmation, ...passwordChangeValues } = values;
    onSubmit(passwordChangeValues);
  };

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <>
      <Form onSubmit={handleSubmit(submit)}>
        {withOldPassword && (
          <FormGroup>
            <Label htmlFor="oldPassword">{t(Keys.oldPassword)}</Label>
            <Input
              type="password"
              id="oldPassword"
              placeholder={t(Keys.oldPassword)}
              invalid={!!errors.oldPassword}
              {...register('oldPassword')}
              onChange={(e): void => {
                setValue('oldPassword', e.target.value);
              }}
              autoComplete="current-password"
            />
            {errors.oldPassword && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.oldPassword.message}
              </FormFeedback>
            )}
          </FormGroup>
        )}
        <FormGroup className="position-relative">
          <Label htmlFor="password">{t(Keys.password)}</Label>
          <Input
            type="password"
            id="password"
            placeholder={t(Keys.password)}
            invalid={!!errors.password}
            {...register('password')}
            onChange={(e): void => {
              setValue('password', e.target.value);
            }}
            autoComplete="new-password"
          />
          {passwordComplexityInfo && (
            <InfoPopover
              className="pw-change-popover"
              popoverClassName="pw-change-popover__container"
              text={
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitize(passwordComplexityInfo)
                  }}
                />
              }
            />
          )}
          {errors.password && (
            <FormFeedback>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {errors.password.message}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label htmlFor="passwordConfirmation">{t(Keys.passwordConfirmation)}</Label>
          <Input
            type="password"
            id="passwordConfirmation"
            placeholder={t(Keys.passwordConfirmation)}
            invalid={!!errors.passwordConfirmation}
            {...register('passwordConfirmation')}
            onChange={(e): void => {
              setValue('passwordConfirmation', e.target.value);
            }}
            autoComplete="new-password"
          />
          {errors.passwordConfirmation && (
            <FormFeedback>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {errors.passwordConfirmation.message}
            </FormFeedback>
          )}
        </FormGroup>
        <Row className="justify-content-end">
          <Col xs="12" md="auto">
            <Button block color="primary" type="submit">
              {t(Keys.submit)}
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default PasswordChangeForm;
